<template>
  <model-form
    ref="modelForm"
    :model="orderItem"
    modal-type="modal"
    @change="order.fetch(true)"
  >
    <q-card
      flat
      bordered
    >
      <q-card-section>
        <q-banner class="text-white bg-warning">
          <div class="text-h6">
            <q-icon name="warning" />Warning
          </div>
          Making changes to these dates impact revenue recognition and payment
          reconciliation for this order item.
          <br><br>
          Only make changes if you are well versed in the implications of these changes.
          <br><br>
          These three dates are used to determine the state of the Order Item.
        </q-banner>
        <div class="q-table__title q-ml-none q-mt-md">
          Accounting Days
        </div>
        <q-chip
          v-for="(locked, date) in order.accountingDatesLocked"
          :key="date"
        >
          <q-avatar
            :icon="locked ? 'lock' : 'lock_open'"
            :color="locked ? 'red' : 'green'"
          />
          {{ IsoDate.parse(date).i18n('shortDate') }}
          <q-tooltip v-if="locked">
            This accounting day is locked and is limiting the ability to make changes.<br>
            Unlock the accounting day to enable more editing options.
          </q-tooltip>
        </q-chip>
      </q-card-section>
      <q-card-section>
        <div class="q-table__title q-mb-xs">
          Dates
        </div>
        <div class="column q-gutter-sm">
          <q-select
            v-model="orderItem.placedAt"
            :clearable="placedAtClearable"
            class="col"
            label="Placed"
            emit-value
            map-options
            :options="allDateOptions"
            :disable="placedAtLocked"
          >
            <template #selected-item="scope">
              {{ formatTimestamp(scope.opt) }}
            </template>
          </q-select>
          <q-select
            v-model="orderItem.paidAt"
            :clearable="paidAtClearable"
            class="col"
            label="Paid"
            emit-value
            map-options
            :disable="!orderItem.placedAt || paidAtLocked"
            :options="allDateOptions"
          >
            <template #selected-item="scope">
              {{ formatTimestamp(scope.opt) }}
            </template>
          </q-select>
          <q-select
            v-model="orderItem.voidedAt"
            :clearable="voidedAtClearable"
            class="col"
            label="Voided"
            emit-value
            map-options
            :disable="!orderItem.placedAt || voidedAtLocked"
            :options="voidDateOptions"
          >
            <template #selected-item="scope">
              {{ formatTimestamp(scope.opt) }}
            </template>
          </q-select>
        </div>
      </q-card-section>
    </q-card>
  </model-form>
</template>

<script>

import { formatTime, IsoDate, TIMEZONE_MODES, timezoneMode } from 'src/models/model'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const DATE_FIELDS = ['placedAt', 'paidAt', 'voidedAt']

export default {
  name: 'OrderItemDateForm',
  components: {
    ModelForm
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  computed: {
    allDateOptions () {
      // Only let the user select times that match another times in the order
      return _([this.order.orderItems.data, this.order.payments.data])
        .flatten()
        .map(item => _.map(DATE_FIELDS, (dateField) => item[dateField]))
        .flatten()
        .compact()
        .uniqBy(date => date.getTime())
        .filter(date => {
          // Only allow dates that are not locked
          const dateString = formatTime(date, 'dash', this.nonRelativeMode)
          return !this.order.accountingDatesLocked[dateString]
        })
        .sort()
        .map(date => ({
          label: this.formatTimestamp(date),
          value: date
        }))
        .value()
    },
    voidDateOptions () {
      if (!this.effectiveAtLocked) return this.allDateOptions

      // Only allow voided dates that are after the effectiveAt date if effective date is locked
      return this.allDateOptions.filter(date => date.value > this.orderItem.effectiveAt)
    },
    nonRelativeMode () {
      return timezoneMode.value === TIMEZONE_MODES.RELATIVE ? TIMEZONE_MODES.SITE : timezoneMode.value
    },
    placedAtClearable () {
      if (this.placedAtLocked) return false

      if (this.orderItem.paidAt || this.orderItem.voidedAt) {
        return false
      }

      // If the effectiveAt date is locked the placedAt date cannot be cleared
      if (this.effectiveAtLocked) {
        return false
      }

      return true
    },
    paidAtClearable () {
      if (this.paidAtLocked) return false

      if (this.effectiveAtLocked) {
        return this.orderItem.paidAt > this.orderItem.effectiveAt
      }

      return true
    },
    voidedAtClearable () {
      if (this.voidedAtLocked) return false

      if (this.effectiveAtLocked) {
        return this.orderItem.voidedAt > this.orderItem.effectiveAt
      }

      return true
    },
    placedAtLocked () {
      return this.dateLockStatus(this.orderItem.placedAt)
    },
    paidAtLocked () {
      return this.dateLockStatus(this.orderItem.paidAt)
    },
    voidedAtLocked () {
      return this.dateLockStatus(this.orderItem.voidedAt)
    },
    effectiveAtLocked () {
      return this.dateLockStatus(this.orderItem.effectiveAt)
    }
  },
  models: {
    orderItem: {
      itemOrderItem: {
        itemVariation: true,
        itemModifiers: true,
        item: true
      },
      ticket: {
        showing: {
          movie: 'default',
          screen: true
        },
        ticketType: true,
        user: 'default'
      },
      voucherPurchase: {
        voucherType: true,
        card: 'default'
      },
      tax: true,
      discount: true,
      voucher: 'default',
      voucherType: true,
      giftCard: true,

      order: 'default',
      canVoid: 'default',
      cannotVoidReason: 'default',
      collectedUser: {
        fields: ['id', 'firstName', 'lastName']
      },
      orderItemBookingFee: true
    }
  },
  context: ['currentUser'],
  created () {
    _.extend(this, {
      IsoDate
    })
  },

  methods: {
    show (model) {
      this.$refs.modelForm.show(model)
    },
    formatTimestamp (date) {
      // Create a details format that includes both absolute time and relative time

      // If this is an object with a label then this has already been formatted in the allDateOptions filter and we can just use the label
      if (typeof date === 'object' && 'label' in date) {
        return date.label
      }
      return `${formatTime(date, 'extraLong', this.nonRelativeMode)} (${formatTime(date, 'extraLong', TIMEZONE_MODES.RELATIVE)})`
    },
    dateLockStatus (date) {
      return this.order.accountingDatesLocked[formatTime(date, 'dash', this.nonRelativeMode)]
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
